import { forwardRef, type CSSProperties } from 'react'
import { cn } from '@/utils/cn'
import { getStageColor } from '@/components/Deals/deals-constants'
import type { JourneyMacroStage } from '@/components/Deals/dealsJourney.constants'
import {
  isJourneyMacroDropAllowed,
  resolveJourneyDropStage,
  resolveJourneyMacroIdForDeal,
} from '@/components/Deals/dealsJourney.util'
import { getJourneyColumnMetrics } from '@/components/Deals/journeyColumnMetrics'
import { JourneyColumnStats } from '@/components/Deals/JourneyColumnStats'
import { DealsJourneyRow } from '@/components/Deals/DealsJourneyRow'
import type { useDealsDnd } from '@/hooks/useDealsDnd'
import type { Deal, DealStage } from '@/types'

type DndState = ReturnType<typeof useDealsDnd>

export type JourneyColumnProps = {
  macro: JourneyMacroStage
  deals: Deal[]
  onSelectDeal: (deal: Deal) => void
  onMoveStage?: (dealId: string, newStage: DealStage) => void
  areValuesVisible?: boolean
  dnd?: DndState
  draggedDeal?: Deal | null
  userRole?: string | null
}

export const JourneyColumn = forwardRef<HTMLDivElement, JourneyColumnProps>(
  function JourneyColumn(
    {
      macro,
      deals,
      onSelectDeal,
      onMoveStage,
      areValuesVisible = false,
      dnd,
      draggedDeal,
      userRole,
    },
    ref,
  ) {
    const metrics = getJourneyColumnMetrics(deals)
    const dropStage = draggedDeal
      ? resolveJourneyDropStage(draggedDeal, macro, userRole)
      : (macro.etapas[0] ?? null)
    const isSameColumn = Boolean(
      dnd?.isDragging && draggedDeal && resolveJourneyMacroIdForDeal(draggedDeal) === macro.id,
    )
    const isAllowed = Boolean(
      draggedDeal && isJourneyMacroDropAllowed(draggedDeal, macro, userRole),
    )
    const isDragOver = Boolean(dnd && dropStage && dnd.draggedOverStage === dropStage)
    const isInvalidDrop = Boolean(dnd?.isDragging && (isSameColumn || !isAllowed))

    return (
      <div
        ref={ref}
        id={`journey-col-${macro.id}`}
        className={cn(
          'deals-journey-column flex min-w-0 flex-1 flex-col gap-2.5 rounded-2xl p-1',
          dnd?.isDragging && 'kanban-column-dnd',
          isDragOver && !isInvalidDrop && 'deals-v2-column--drop-ready kanban-column-active',
          isDragOver && isInvalidDrop && 'deals-v2-column--drop-invalid kanban-column-invalid',
        )}
        style={{ '--stage-color': macro.color } as CSSProperties}
        aria-labelledby={`journey-${macro.id}`}
        onDragOver={dnd?.handleDragOver}
        onDragEnter={dropStage && dnd ? (e) => dnd.handleDragEnter(e, dropStage) : undefined}
        onDragLeave={dropStage && dnd ? (e) => dnd.handleDragLeave(e, dropStage) : undefined}
        onDrop={dropStage && dnd ? (e) => dnd.handleDrop(e, dropStage) : undefined}
      >
        <header className='deals-v2-column-header shrink-0 overflow-hidden rounded-2xl border border-[var(--border)] p-3 shadow-sm'>
          <div className='flex items-start justify-between gap-2'>
            <div className='min-w-0'>
              <h3
                id={`journey-${macro.id}`}
                className='truncate text-sm font-semibold text-text-primary'
              >
                {macro.label}
              </h3>
              <p
                className={cn(
                  'mt-0.5 text-[11px] font-medium',
                  metrics.atRisk > 0 ? 'text-warning' : 'text-text-muted',
                )}
              >
                {metrics.count > 0 ? metrics.statusLine : macro.hint}
              </p>
            </div>
            <span
              className='rounded-full px-2 py-0.5 text-xs font-bold tabular-nums text-white'
              style={{ backgroundColor: macro.color }}
            >
              {metrics.count}
            </span>
          </div>

          <JourneyColumnStats metrics={metrics} areValuesVisible={areValuesVisible} />
        </header>

        {dnd?.isDragging ? (
          <p
            className={cn(
              'px-1 text-[10px] font-medium',
              isInvalidDrop ? 'text-danger' : 'text-accent',
            )}
          >
            {isSameColumn ? 'Coluna atual' : isAllowed ? 'Solte para avançar' : 'Transição bloqueada'}
          </p>
        ) : null}

        <div className='deals-journey-column-body flex flex-col gap-1.5 px-0.5 pb-1'>
          {deals.map((deal) =>
            onMoveStage && dnd ? (
              <div
                key={deal.id}
                draggable
                onDragStart={(e) => dnd.handleDragStart(e, deal.id)}
                onDragEnd={dnd.handleDragEnd}
                className='cursor-grab active:cursor-grabbing'
              >
                <DealsJourneyRow
                  deal={deal}
                  journeyColor={macro.color}
                  stageColor={getStageColor(deal.etapa)}
                  onClick={() => onSelectDeal(deal)}
                  areValuesVisible={areValuesVisible}
                  onMoveStage={onMoveStage}
                />
              </div>
            ) : (
              <DealsJourneyRow
                key={deal.id}
                deal={deal}
                journeyColor={macro.color}
                stageColor={getStageColor(deal.etapa)}
                onClick={() => onSelectDeal(deal)}
                areValuesVisible={areValuesVisible}
                onMoveStage={onMoveStage}
              />
            ),
          )}

          {deals.length === 0 && !dnd?.isDragging ? (
            <div className='rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-dark)]/30 px-4 py-10 text-center'>
              <p className='text-xs text-text-muted'>Nenhuma negociação</p>
            </div>
          ) : null}

          {deals.length === 0 && dnd?.isDragging ? (
            <div
              className={cn(
                'kanban-drop-placeholder rounded-xl',
                isDragOver && !isInvalidDrop && 'active',
                isInvalidDrop && 'invalid',
              )}
            >
              <p>{isInvalidDrop ? 'Inválido' : 'Solte aqui'}</p>
            </div>
          ) : null}
        </div>
      </div>
    )
  },
)
